/**
 * Fleet presence and quick messaging.
 *
 * Two jobs:
 *  - broadcast where this driver is, and keep a roster of everyone else with
 *    the bearing and distance to them,
 *  - carry three fixed questions between drivers ("food?", "fuel stop?",
 *    "help!") and their yes/no answers.
 *
 * The wire format is an ENUM, never a sentence. 'food' | 'fuel' | 'help' goes
 * over the network and each app renders and speaks it in ITS OWN language, so a
 * Thai-speaking driver receives Thai even though the sender was on English.
 * Sending prose would have forced the sender's language on the recipient and
 * pushed foreign script through the wrong TTS voice - exactly the failure this
 * release exists to remove.
 *
 * Everything here is best-effort. The backend is polled, not streamed (shared
 * hosting, no websockets); a failed poll is silent and the next one recovers.
 */

import type { Fix, LngLat } from '../core/types';
import { bearing, bearingDelta, distance } from '../core/geo';

const API = `${import.meta.env.BASE_URL}api`;

export type MessageKind = 'food' | 'fuel' | 'help';
export type MessageReply = 'yes' | 'no';

export interface FleetDriver {
  id: number;
  name: string;
  lng: number;
  lat: number;
  heading: number | null;
  speed: number | null;
  destination: string | null;
  /** epoch ms of their last report */
  at: number;
}

/** A driver plus where they are relative to US. */
export interface FleetContact extends FleetDriver {
  /** metres, straight line */
  distance: number;
  /** compass bearing from us to them */
  bearing: number;
  /** bearing relative to our heading: 0 = dead ahead, 180 = behind */
  relativeBearing: number;
  /** true when their last report is old enough to distrust */
  stale: boolean;
}

export interface QuickMessage {
  id: string;
  from: number;
  fromName: string;
  to: number;
  kind: MessageKind;
  reply: MessageReply | null;
  at: number;
  repliedAt: number | null;
}

/** Reports older than this are shown greyed out rather than trusted. */
const STALE_MS = 120_000;

export interface FleetEvents {
  /** Roster changed. */
  onDrivers?: (contacts: FleetContact[]) => void;
  /** A question has arrived for us and needs a yes/no. */
  onIncoming?: (message: QuickMessage) => void;
  /**
   * Someone answered a question we asked. `announce` is false when the answer
   * is only being adopted into the roster (backlog on a cold start), true when
   * it genuinely just arrived and deserves the driver's attention.
   */
  onReply?: (message: QuickMessage, announce: boolean) => void;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    signal: AbortSignal.timeout(12_000),
    ...init,
  });
  if (!res.ok) throw new Error(String(res.status));
  return (await res.json()) as T;
}

export class Fleet {
  private drivers: FleetDriver[] = [];
  private contacts: FleetContact[] = [];
  private lastFix: Fix | null = null;
  private since = 0;
  private seenIncoming = new Set<string>();
  private seenReplies = new Set<string>();
  /** False until the first poll lands; see pollMessages for why that matters. */
  private primed = false;
  private timers: number[] = [];
  private destinationName: string | null = null;

  constructor(private events: FleetEvents = {}) {}

  start(): void {
    this.stop();
    // Position out, roster in. 20 s is frequent enough to feel live in a
    // convoy without being chatty on a mobile connection.
    this.timers.push(window.setInterval(() => void this.push(), 20_000));
    this.timers.push(window.setInterval(() => void this.pull(), 20_000));
    // Messages need to feel immediate, so they get their own faster loop.
    this.timers.push(window.setInterval(() => void this.pollMessages(), 8_000));
    void this.pull();
    void this.pollMessages();
  }

  stop(): void {
    for (const id of this.timers) clearInterval(id);
    this.timers = [];
  }

  /** Feed the latest GPS fix; also recomputes bearings to everyone. */
  setFix(fix: Fix): void {
    this.lastFix = fix;
    this.recompute();
  }

  setDestinationName(name: string | null): void {
    this.destinationName = name;
  }

  get roster(): FleetContact[] {
    return this.contacts;
  }

  private async push(): Promise<void> {
    const f = this.lastFix;
    if (!f) return;
    try {
      await call('/presence', {
        method: 'POST',
        body: JSON.stringify({
          lng: f.lng,
          lat: f.lat,
          heading: f.heading,
          speed: f.speed,
          destination: this.destinationName,
        }),
      });
    } catch {
      /* offline or backend down - the next tick retries */
    }
  }

  private async pull(): Promise<void> {
    try {
      const { drivers } = await call<{ drivers: FleetDriver[] }>('/presence');
      this.drivers = drivers;
      this.recompute();
    } catch {
      /* keep the last known roster rather than blanking the bubble */
    }
  }

  private recompute(): void {
    const f = this.lastFix;
    const here: LngLat | null = f ? [f.lng, f.lat] : null;
    const now = Date.now();

    this.contacts = this.drivers
      .map((d): FleetContact => {
        const there: LngLat = [d.lng, d.lat];
        const b = here ? bearing(here, there) : 0;
        return {
          ...d,
          distance: here ? distance(here, there) : 0,
          bearing: b,
          relativeBearing:
            here && f?.heading != null ? Math.abs(bearingDelta(f.heading, b)) : Math.abs(b),
          stale: now - d.at > STALE_MS,
        };
      })
      .sort((a, b) => a.distance - b.distance);

    this.events.onDrivers?.(this.contacts);
  }

  private async pollMessages(): Promise<void> {
    try {
      const body = await call<{ inbox: QuickMessage[]; replies: QuickMessage[]; now: number }>(
        `/messages?since=${this.since}`,
      );
      this.since = body.now;

      for (const msg of body.inbox) {
        // The backend keeps returning unanswered messages; only surface each once.
        if (this.seenIncoming.has(msg.id)) continue;
        this.seenIncoming.add(msg.id);
        this.events.onIncoming?.(msg);
      }

      /*
       * A fresh app start polls with since=0, so the backend hands back every
       * answer still inside its 2 h retention. Announcing those would tell a
       * driver who just opened the app that "Bobby: Yes" had only now arrived,
       * when it may be an hour stale and about a stop already passed.
       *
       * So the first poll only ADOPTS the backlog - it fills the roster's
       * answer column, which is what makes a missed toast recoverable - and
       * anything genuinely recent still speaks up.
       */
      for (const msg of body.replies) {
        if (this.seenReplies.has(msg.id)) continue;
        this.seenReplies.add(msg.id);
        const fresh = Date.now() - (msg.repliedAt ?? msg.at) < 90_000;
        this.events.onReply?.(msg, this.primed || fresh);
      }
      this.primed = true;
    } catch {
      /* silent - a dropped poll is not worth telling a driver about */
    }
  }

  /** Ask one driver a fixed question. */
  async send(to: number, kind: MessageKind): Promise<void> {
    await call('/messages', { method: 'POST', body: JSON.stringify({ to, kind }) });
  }

  /** Answer a question addressed to us. */
  async reply(id: string, reply: MessageReply): Promise<void> {
    // Mark handled immediately so a slow round-trip can't re-prompt.
    this.seenIncoming.add(id);
    await call(`/messages/${id}/reply`, { method: 'POST', body: JSON.stringify({ reply }) });
  }
}
